// ================================================================
// File: src/pages/CareersPage.jsx  (full file with modal wired)
// ================================================================
import React, { useMemo, useState } from "react";
import "./CareersPage.css";
import Footer from "../components/Footer";
import Header from "../components/Header";
import ImageSection from "../components/ImageSection";
import ApplyModal from "../components/ApplyModal"; // NEW
import logo from "../images/Logo_ClinicStaffing.jpeg";

// ------- DATA
const jobs = [
  { title: "Business Development", location: "Dallas, USA", type: "Full Time (On-site)", category: "Business" },
  { title: "Biller", location: "Dallas, USA", type: "Full Time (On-site)", category: "Admin" },
  { title: "IT Support", location: "Dallas, USA", type: "Full Time (On-site)", category: "IT" },
  { title: "Medical Assistants", location: "Dallas, USA", type: "Full Time (On-site)", category: "Clinical" },
  { title: "Front Office", location: "Dallas, USA", type: "Full Time (On-site)", category: "Admin" },
  { title: "Nurse Practitioners", location: "Dallas, USA", type: "Full Time (On-site)", category: "Clinical" },
  { title: "Customer Service Representative", location: "Dallas, USA", type: "Full Time (On-site)", category: "Business" },
  { title: "Office Manager", location: "Dallas, USA", type: "Full Time (On-site)", category: "Admin" },
  { title: "Nurse Practitioner", location: "Dallas, USA", type: "Full Time (On-site)", category: "Clinical" },
  { title: "Medical Biller", location: "Dallas, USA", type: "Full Time (On-site)", category: "Admin" },
  { title: "Medical Technician", location: "Dallas, USA", type: "Full Time (On-site)", category: "Clinical" },
];

const CATEGORIES = ["All", "Clinical", "Admin", "IT", "Business"];

// ------- BRAND IMAGE with fallback
function BrandTile({ size = 64 }) {
  const [ok, setOk] = useState(true);
  return (
    <div className="brand-tile" style={{ width: size, height: size }}>
      {ok ? (
        <img
          src={logo}
          alt="Ezclinicstaffing"
          onError={() => setOk(false)}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="brand-fallback" aria-label="Ezclinicstaffing">MD</div>
      )}
    </div>
  );
}

// ------- CARD
function JobCard({ title, location, type, category, onApply }) {
  return (
    <article className="job-card" aria-labelledby={`${title}-heading`}>
      <div className="job-card__brand" aria-hidden>
        <BrandTile size={56} />
      </div>
      <div className="job-card__body">
        <div className="job-card__badge">{category}</div>
        <h3 id={`${title}-heading`} className="job-card__title">{title}</h3>
        <div className="job-card__company">Ezclinicstaffing</div>
        <div className="job-card__meta">
          <span>{location}</span>
          <span className="dot" aria-hidden>•</span>
          <span>{type}</span>
        </div>
      </div>
      <div className="job-card__cta">
        <button className="btn btn--primary" onClick={() => onApply(title)}>
          Apply Now
        </button>
      </div>
    </article>
  );
}

// ------- PAGE
export default function CareersPage() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [isApplyOpen, setApplyOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState("");

  const filteredJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => {
      const inCategory = activeCategory === "All" || j.category === activeCategory;
      const inText =
        !q ||
        j.title.toLowerCase().includes(q) ||
        j.location.toLowerCase().includes(q) ||
        j.type.toLowerCase().includes(q) ||
        j.category.toLowerCase().includes(q);
      return inCategory && inText;
    });
  }, [query, activeCategory]);

  const handleOpenApply = (jobTitle) => {
    setSelectedJob(jobTitle);
    setApplyOpen(true);
  };

  const handleSubmitForm = (payload) => {
    // Why: placeholder; replace with NodeMailer fetch() later.
    console.log("Application payload:", payload);
    alert("Application submitted! (Wire backend to actually send email.)");
    setApplyOpen(false);
  };

  return (
    <>
      <Header />
      <ImageSection title="Start Your Career With Ezclinicstaffing" forCarrersPage={true} />

      <header className="careers-hero">
        <div className="container">
          <div className="hero__kicker" aria-hidden>Careers • Jobs</div>
          <h1 className="hero__title">Current Job Openings</h1>
          <p className="hero__subtitle">
            Build your career with a mission-driven team delivering outstanding patient experiences.
          </p>
        </div>
      </header>

      <main className="careers container">
        <section className="careers__intro">
          <div className="intro__copy">
            <h2 className="intro__title">Join Our Team at Ezclinicstaffing</h2>
            <ul className="intro__list">
              <li>Be a vital part of our mission-driven workplace.</li>
              <li>Contribute to a dynamic and collaborative team environment.</li>
              <li>Explore growth opportunities in a forward-thinking organization.</li>
              <li>Enjoy a positive and inclusive work culture.</li>
              <li>Make a meaningful impact on the healthcare industry.</li>
              <li>Engage in ongoing professional development.</li>
              <li>Participate in team-building activities and events.</li>
              <li>Collaborate with experienced professionals in the field.</li>
              <li>Shape the future of healthcare management with us.</li>
            </ul>
          </div>

          <div className="intro__brand-row">
            <BrandTile size={64} />
            <div className="intro__brand-copy">
              <p>We’re scaling clinical and non-clinical teams. Filter roles and apply in seconds.</p>
            </div>
          </div>
        </section>

        {/* Controls */}
        <section className="controls">
          <div className="chips" role="tablist" aria-label="Job categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={activeCategory === c}
                className={`chip ${activeCategory === c ? "chip--active" : ""}`}
                onClick={() => setActiveCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, location, or type…"
              className="search__input"
              aria-label="Search jobs"
            />
            <div className="search__count" aria-live="polite">
              {filteredJobs.length} role{filteredJobs.length !== 1 ? "s" : ""}
            </div>
          </div>
        </section>

        {/* Jobs grid */}
        <section className="careers__grid" aria-label="Open positions">
          {filteredJobs.map((j) => (
            <JobCard
              key={`${j.title}-${j.category}`}
              title={j.title}
              location={j.location}
              type={j.type}
              category={j.category}
              onApply={handleOpenApply}
            />
          ))}
          {filteredJobs.length === 0 && (
            <div className="empty">
              <p>No roles match your filters. Try a different search.</p>
              <button className="btn btn--ghost" onClick={() => { setQuery(""); setActiveCategory("All"); }}>
                Reset filters
              </button>
            </div>
          )}
        </section>
      </main>

      {/* Modal */}
      <ApplyModal
        open={isApplyOpen}
        jobTitle={selectedJob}
        onClose={() => setApplyOpen(false)}
        onSubmit={handleSubmitForm}
      />

      <Footer />
    </>
  );
} 